import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import useThemeStore from "@/shared/stores/useThemeStore";
import { useCurrentTalentProfile } from "@/shared";
import {
  useAddCardToCharmCards,
  useRemoveCardFromCharmCards,
} from "@/modules/charm/api/charm-cards.api";

export type SaveCardProps = {
  mainCardId: number;
  /** When true, shows Remove instead of Save. */
  deckStatus?: boolean;
  buttonSx?: SxProps<Theme>;
  size?: "small" | "medium" | "large";
};

const defaultButtonSx: SxProps<Theme> = {
  background: "var(--button-gradient)",
  color: "var(--neutralWhite)",
  textTransform: "none",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  py: 0.65,
  px: 1.5,
  minWidth: 0,
  flexShrink: 0,
  borderRadius: "8px",
};

export function SaveCard({
  mainCardId,
  deckStatus = false,
  buttonSx,
  size = "small",
}: SaveCardProps) {
  const { t } = useTranslation("taascard");
  const queryClient = useQueryClient();
  const { darkMode } = useThemeStore();
  const { currentProfile } = useCurrentTalentProfile();

  const talentHeaders = currentProfile?.id ? { "talent-id": String(currentProfile.id) } : undefined;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const isSaved = Boolean(deckStatus);

  const { update: addCardToCharmCards, isLoading: isAddingCardToCharm } = useAddCardToCharmCards({
    key: ["add-card-to-charm-cards", mainCardId],
    params: {
      headers: talentHeaders,
    },
  });

  const { update: removeCardFromCharmCards, isLoading: isRemoving } = useRemoveCardFromCharmCards({
    key: ["remove-card-from-charm-cards", mainCardId],
    params: {
      main_card_id: mainCardId,
      headers: talentHeaders,
    },
  });

  const isBusy = isAddingCardToCharm || isRemoving;
  const descColor = darkMode ? "rgba(255,255,255,0.65)" : "rgba(30,20,60,0.68)";
  const titleColor = darkMode ? "#fafafa" : "#1e1438";
  const orbBorder = darkMode ? "rgba(250,204,21,0.22)" : "rgba(120,53,15,0.30)";

  const invalidateCardQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["talent-deck-main-cards", mainCardId],
    });

    queryClient.invalidateQueries({
      queryKey: ["main-card-by-id", mainCardId],
    });

    queryClient.invalidateQueries({
      queryKey: ["card-thumbnail-by-id", mainCardId],
    });
  };

  const handleConfirm = async () => {
    if (!Number.isFinite(mainCardId) || mainCardId <= 0 || isBusy) {
      return;
    }

    if (isSaved) {
      await removeCardFromCharmCards(
        {
          main_card_id: mainCardId,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("save_card_remove_success"), {
              variant: "success",
            });

            setConfirmOpen(false);
            invalidateCardQueries();
          },

          onError: (err: any) => {
            enqueueSnackbar(
              err?.response?.data?.message ?? err?.message ?? t("save_card_remove_failed"),
              {
                variant: "error",
              }
            );
          },
        }
      );

      return;
    }

    await addCardToCharmCards(
      {
        main_card_id: mainCardId,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t("save_card_save_success"), {
            variant: "success",
          });

          setConfirmOpen(false);
          invalidateCardQueries();
        },

        onError: (err: any) => {
          enqueueSnackbar(err?.response?.data?.message ?? err?.message ?? t("save_card_save_failed"), {
            variant: "error",
          });
        },
      }
    );
  };

  if (!Number.isFinite(mainCardId) || mainCardId <= 0) {
    return null;
  }

  const mergedButtonSx = [defaultButtonSx, buttonSx] as SxProps<Theme>;

  return (
    <>
      <Button
        variant="contained"
        size={size}
        disabled={isBusy}
        onClick={() => setConfirmOpen(true)}
        sx={mergedButtonSx}
      >
        {isRemoving
          ? t("save_card_removing")
          : isAddingCardToCharm
            ? t("save_card_saving")
            : isSaved
              ? t("save_card_remove")
              : t("save_card_save_to_charm")}
      </Button>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          if (!isBusy) {
            setConfirmOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            background: darkMode
              ? "linear-gradient(160deg, #18181b 0%, #1e1b2e 55%, #1a1a27 100%)"
              : "linear-gradient(160deg, #f5f3fa 0%, #efeaf8 50%, #f7f1e6 100%)",
            border: `1px solid ${orbBorder}`,
            boxShadow: darkMode
              ? "0 18px 48px rgba(0,0,0,0.55)"
              : "0 18px 48px rgba(91,33,182,0.16)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 800,
            fontSize: "1.1rem",
            color: titleColor,
            pb: 1,
          }}
        >
          {isSaved ? t("save_card_remove_title") : t("save_card_save_title")}
        </DialogTitle>

        <DialogContent sx={{ pt: 0.5 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              lineHeight: 1.6,
              color: descColor,
            }}
          >
            {isSaved
              ? t("save_card_remove_confirm_body")
              : t("save_card_save_confirm_body")}
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={isBusy}
            sx={{
              textTransform: "none",
              color: descColor,
              fontWeight: 600,
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleConfirm()}
            disabled={isBusy}
            sx={{
              ...defaultButtonSx,
              px: 2,
            }}
          >
            {isBusy ? (isSaved ? t("save_card_removing") : t("save_card_saving")) : t("save_card_confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SaveCard;
